import React, { useRef } from 'react';
import { motion } from 'motion/react';
import { Settings, Palette, Maximize2, MicOff, Mic, X, Headphones, HeadphoneOff, Video, VideoOff, ChevronDown } from 'lucide-react';

interface UserPanelProps {
  userProfile: any;
  currentUser: any;
  isMuted: boolean;
  toggleMute: () => void;
  isDeafened: boolean;
  toggleDeafen: () => void;
  isVideoOn: boolean;
  toggleVideo: () => void;
  toggleFullscreen: () => void;
  showUserSettings: boolean;
  setShowUserSettings: (show: boolean) => void;
  showUiConfig: boolean;
  setShowUiConfig: (show: boolean) => void;
  bgTheme: string;
  setBgTheme: (theme: 'synthwave' | 'aurora' | 'day' | 'ocean' | 'classic' | 'aeroform' | 'monolith' | 'catppuccin' | 'tokyonight') => void;
  baseHue: number;
  setBaseHue: (hue: number) => void;
  getUserFontStyle: (userId: string | undefined) => React.CSSProperties;
  onAvatarClick: () => void;
  setViewingProfileId: (id: string | null) => void;
  setUserProfile?: React.Dispatch<React.SetStateAction<any>>;
}

export default function UserPanel({
  userProfile,
  currentUser,
  isMuted,
  toggleMute,
  isDeafened,
  toggleDeafen,
  isVideoOn,
  toggleVideo,
  toggleFullscreen,
  showUserSettings,
  setShowUserSettings,
  showUiConfig,
  setShowUiConfig,
  bgTheme,
  setBgTheme,
  baseHue,
  setBaseHue,
  getUserFontStyle,
  onAvatarClick,
  setViewingProfileId,
  setUserProfile,
}: UserPanelProps) {
  const uiConfigRef = useRef<HTMLDivElement>(null);
  const [showPresenceMenu, setShowPresenceMenu] = React.useState(false);
  const [showAccountMenu, setShowAccountMenu] = React.useState(false);

  // Close UI config on click outside could be handled in App, but we can just use React state
  return (
    <div className="px-4 py-4 bg-white/5 border-t border-white/5 flex flex-col gap-3 group relative shrink-0">
      {/* User Profile Info Row */}
      <div className="flex items-center gap-3 min-w-0 w-full relative">
        <div
          className="neon-avatar-frame w-10 h-10 overflow-hidden cursor-pointer transition-all shrink-0"
          onClick={() => setShowAccountMenu(!showAccountMenu)}
        >
          <img src={userProfile.avatar} className="w-full h-full object-cover" alt="User avatar" />
        </div>

        {/* Account Menu Dropdown */}
        {showAccountMenu && (
          <div className="absolute left-0 bottom-full mb-2 w-48 bg-[#0b0e1a]/95 backdrop-blur-3xl border border-white/10 rounded-xl p-2 z-[10000] shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col gap-1">
            <button
              onClick={() => { onAvatarClick(); setShowAccountMenu(false); }}
              className="w-full text-left px-4 py-3 hover:bg-white/10 rounded-lg text-white text-sm font-bold transition-colors flex items-center gap-2"
            >
              <span>👤</span> View Profile
            </button>
            <button
              onClick={() => { setShowUserSettings(true); setShowAccountMenu(false); }}
              className="w-full text-left px-4 py-3 hover:bg-white/10 rounded-lg text-white text-sm font-bold transition-colors flex items-center gap-2"
            >
              <span>⚙️</span> Settings
            </button>
            <div className="h-[1px] bg-white/10 my-1"></div>
            <button
              className="w-full text-left px-4 py-3 hover:bg-white/10 rounded-lg text-white text-sm font-bold transition-colors flex items-center gap-2"
            >
              <span>🔄</span> Switch Accounts
            </button>
          </div>
        )}
        <div className="flex flex-col min-w-0 flex-1 relative">
          <div 
            className="text-[13px] font-black text-white uppercase tracking-wider leading-tight truncate w-full cursor-pointer hover:text-[var(--neon)] transition-colors" 
            title={userProfile.name} 
            style={getUserFontStyle(currentUser?.id)}
            onClick={() => setViewingProfileId('me')}
          >
            {userProfile.name}
          </div>
          <div 
            className="flex items-center gap-1.5 mt-1 cursor-pointer bg-white/5 hover:bg-white/10 w-fit rounded-md px-1.5 py-0.5 transition-colors border border-white/5"
            onClick={() => setShowPresenceMenu(!showPresenceMenu)}
          >
            <span className={`w-2 h-2 rounded-full shadow-[0_0_8px_currentColor] shrink-0 ${
              userProfile.presence === 'Idle' ? 'text-amber-500 bg-amber-500 animate-pulse' :
              userProfile.presence === 'Away' ? 'text-amber-500/70 bg-amber-500/70' :
              userProfile.presence === 'Do Not Disturb' ? 'text-red-500 bg-red-500' :
              userProfile.presence === 'Invisible' ? 'text-white/30 bg-white/30 shadow-none' :
              'text-[var(--accent-success)] bg-[var(--accent-success)]'
            }`}></span>
            <span className={`text-[9px] font-bold uppercase tracking-[0.15em] truncate ${userProfile.presence === 'Online' || !userProfile.presence ? 'live-indicator-pulse' : 'text-white/40'}`}>
              {userProfile.presence || 'Online'}
            </span>
            <ChevronDown size={10} className="text-white/40 ml-0.5" />
          </div>
          
          {/* Presence Dropdown Menu */}
          {showPresenceMenu && setUserProfile && (
            <div className="absolute left-0 bottom-full mb-1 w-40 bg-[#0b0e1a]/95 backdrop-blur-3xl border border-white/10 rounded-xl p-2 z-[10000] shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col gap-1">
              {[
                { id: 'Online', label: 'Online', color: 'text-[var(--accent-success)] bg-[var(--accent-success)] shadow-[0_0_8px_var(--accent-success)]' },
                { id: 'Idle', label: 'Idle', color: 'text-amber-500 bg-amber-500 shadow-[0_0_8px_#f59e0b]' },
                { id: 'Do Not Disturb', label: 'Do Not Disturb', color: 'text-red-500 bg-red-500 shadow-[0_0_8px_#ef4444]' },
                { id: 'Invisible', label: 'Invisible', color: 'text-white/30 bg-white/30' }
              ].map(status => (
                <button
                  key={status.id}
                  onClick={() => {
                    setUserProfile((prev: any) => ({ ...prev, presence: status.id }));
                    setShowPresenceMenu(false);
                  }}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-all text-left"
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${status.color}`}></span>
                  <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider">{status.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center justify-between w-full bg-black/20 rounded-xl p-1 border border-white/5">
        <button
          onClick={toggleMute}
          className={`flex-1 flex justify-center py-2 px-1 rounded-lg transition-all ${isMuted ? 'bg-red-500/20 text-red-500' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
          title={isMuted ? "Unmute" : "Mute"}
        >            {isMuted ? <MicOff size={18} className="neon-icon-glow" /> : <Mic size={18} className="neon-icon-glow" />}
        </button>
        <button
          onClick={toggleDeafen}
          className={`flex-1 flex justify-center py-2 px-1 rounded-lg transition-all ${isDeafened ? 'bg-red-500/20 text-red-500' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
          title={isDeafened ? "Undeafen" : "Deafen"}
        >            {isDeafened ? <HeadphoneOff size={18} className="neon-icon-glow" /> : <Headphones size={18} className="neon-icon-glow" />}
        </button>
        <button
          onClick={toggleVideo}
          className={`flex-1 flex justify-center py-2 px-1 rounded-lg transition-all ${isVideoOn ? 'bg-[var(--neon)]/20 text-[var(--neon)]' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
          title={isVideoOn ? "Turn off camera" : "Turn on camera"}
        >            {isVideoOn ? <Video size={18} className="neon-icon-glow" /> : <VideoOff size={18} className="neon-icon-glow" />}
        </button>
        <button
          onClick={toggleFullscreen}
          className="flex-1 flex justify-center py-2 px-1 rounded-lg transition-all text-white/40 hover:text-white hover:bg-white/10"
          title="Toggle Fullscreen"
        >            <Maximize2 size={18} className="neon-icon-glow" />
        </button>
        <div className="relative flex-1 flex justify-center" ref={uiConfigRef}>
          <button 
            onClick={() => setShowUiConfig(!showUiConfig)} 
            className={`w-full py-2 px-1 rounded-lg transition-all flex items-center justify-center ${showUiConfig ? 'bg-[var(--neon)] text-black shadow-[0_0_10px_var(--neon)]' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
            title="UI Configuration"
          >
            <Palette size={18} className="neon-icon-glow" />
          </button>
          
          {showUiConfig && (
            <motion.div 
              initial={{ opacity: 0, x: -20, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -20, scale: 0.95 }}
              className="fixed top-20 left-4 md:left-80 md:ml-4 w-auto md:w-[280px] bg-[#0b0e1a]/95 backdrop-blur-3xl border border-white/10 rounded-3xl p-6 z-[10000] shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
            >
              <div className="flex items-center justify-between mb-6 border-b border-white/5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-[var(--neon)]/10">
                    <Palette size={16} className="text-[var(--neon)]" />
                  </div>
                  <h3 className="text-xs font-black tracking-[0.2em] text-white uppercase">Neural Config</h3>
                </div>
                <button onClick={() => setShowUiConfig(false)} className="md:hidden text-white/40"><X size={18} /></button>
              </div>

              <div className="mb-8">
                <label className="text-[9px] font-black text-white/20 uppercase tracking-[0.3em] block mb-4">Aesthetic Theme</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'synthwave', label: 'SYNTHWAVE' },
                    { id: 'aurora', label: 'AURORA' },
                    { id: 'day', label: 'DAY MODE' },
                    { id: 'ocean', label: 'OCEAN SKY' },
                    { id: 'classic', label: 'CLASSIC' },
                    { id: 'aeroform', label: 'AEROFORM' },
                    { id: 'monolith', label: 'MONOLITH' },
                    { id: 'catppuccin', label: 'CATPPUCCIN' },
                    { id: 'tokyonight', label: 'TOKYO NIGHT' }
                  ].map(theme => (
                    <button 
                      key={theme.id}
                      onClick={() => setBgTheme(theme.id as any)}
                      className={`text-[9px] py-3 rounded-xl border font-black tracking-widest transition-all duration-300 ${(theme as any).colSpan ? 'col-span-2 ' : ''}${bgTheme === theme.id ? 'border-[var(--neon)] text-black bg-[var(--neon)] shadow-[0_0_15px_var(--neon)] scale-[1.02]' : 'border-white/5 text-white/30 bg-white/5 hover:bg-white/10 hover:text-white hover:border-white/20'}`}
                    >
                      {theme.label}
                    </button>
                  ))}
                </div>
              </div>

              {['synthwave', 'aurora', 'ocean', 'day', 'classic'].includes(bgTheme) && (
                <div className="bg-white/5 rounded-2xl p-4 border border-white/5 relative">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-[9px] font-black text-white/20 uppercase tracking-[0.3em]">Neural Hue</label>
                    <span className="text-[10px] font-mono text-[var(--neon)] font-bold">{baseHue}°</span>
                  </div>
                  <div className="relative h-2.5 rounded-full mb-1">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-green-500 to-blue-500 rounded-full opacity-30" />
                    <input 
                      type="range" 
                      min="0" 
                      max="360" 
                      value={baseHue} 
                      onChange={(e) => {
                        const newHue = parseInt(e.target.value);
                        setBaseHue(newHue);
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className="absolute top-0 h-full bg-[var(--neon)] rounded-full shadow-[0_0_10px_var(--neon)]" style={{ width: '4px', left: `${(baseHue/360)*100}%` }} />
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
        <button
          onClick={() => setShowUserSettings(!showUserSettings)}
          className={`flex-1 flex justify-center py-2 px-1 rounded-lg transition-all flex items-center justify-center ${showUserSettings ? 'bg-[var(--neon)] text-black shadow-[0_0_10px_var(--neon)]' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
          title="User Settings"
        >
          <Settings size={18} className="neon-icon-glow" />
        </button>
      </div>
    </div>
  );
}
